<template>
  <module-page v-bind="pageParams" :customBreadcrumbs="customBreadcrumbs">
    <v-tabs class="page__tabs" hide-slider>
      <v-tab
        :key="i"
        v-for="(contract, i) in allowedContracts"
        active-class="financial--background white--text opaque"
        @click="getStatus(contract)"
      >
        <span style="width: max-content">{{ getContractTitle(contract) }}</span>
      </v-tab>
    </v-tabs>

    <div class="financial__base-date" v-if="financialPositionData.baseDate">
      <div>
        {{ $t('pages.financialPosition.baseDate') }}:
        {{ formatDate(financialPositionData.baseDate) }}
        <span v-if="!mobile" class="financial__base-date__description">
          {{ pageParams.texts.baseDateDescription }}
        </span>
      </div>

      <button
        @click="print"
        class="financial__base-date__print-btn financial--background white--text no-print"
      >
        <i class="bexsicon-printer icon"></i>
      </button>
    </div>

    <div v-if="mobile" class="financial__base-date__description">
      {{ pageParams.texts.baseDateDescription }}
    </div>

    <div v-if="financialPositionError" class="empty-state">
      {{ $t('responses.error') }}
    </div>

    <div v-else>
      <v-card class="financial__status-iframe">
        <iframe
          v-if="integrations.JUNIX_STATUS_URL"
          :src="`${integrations.JUNIX_STATUS_URL}?numeroContrato=${selectedContract.id}`"
          class="financial__status-iframe__iframe"
        ></iframe>
      </v-card>

      <v-card class="mt-4">
        <v-card-title class="topic__title financial--color">
          {{ $t('pages.financialPosition.financialResume') }}
        </v-card-title>

        <v-card-text class="topic__body">
          <screen-loading
            v-if="totalsLoading"
            margin="20px 25%"
          ></screen-loading>

          <v-layout v-else row wrap>
            <v-flex class="financial__totals" xs12 md4 lg4>
              <screen-loading
                v-if="financialPositionData.paidTotal == null"
                margin="20px 25%"
              ></screen-loading>

              <div v-else class="financial__totals__content">
                {{ $t('pages.financialPosition.totalPaid') }}:
                <div class="financial__totals__value financial--color">
                  {{ formatMoney(financialPositionData.paidTotal) }}
                </div>

                <div
                  v-if="texts.financialNominalValue"
                  class="financial__totals__notation"
                >
                  {{ texts.financialNominalValue }}
                </div>
              </div>
            </v-flex>

            <v-flex class="financial__totals" xs12 md4 lg4>
              <screen-loading
                v-if="financialPositionData.toExpireTotal == null"
                margin="20px 25%"
              ></screen-loading>

              <div v-else class="financial__totals__content">
                {{ $t('pages.financialPosition.toExpire') }}:
                <div class="financial__totals__value">
                  {{ formatMoney(financialPositionData.toExpireTotal) }}
                </div>
              </div>
            </v-flex>

            <v-flex class="financial__totals" xs12 md4 lg4>
              <screen-loading
                v-if="financialPositionData.lateTotal == null"
                margin="20px 25%"
              ></screen-loading>

              <div v-else class="financial__totals__content">
                {{ $t('pages.financialPosition.late') }}:
                <div class="financial__totals__value error--text">
                  {{ formatMoney(financialPositionData.lateTotal) }}
                </div>
              </div>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>

      <v-card class="mt-4 financial-card">
        <v-card-title class="topic__title error--text">
          {{ $t('pages.financialPosition.lateValues') }}
        </v-card-title>

        <v-card-text class="topic__body">
          <screen-loading v-if="lateLoading" margin="20px 25%"></screen-loading>

          <template v-else>
            <div
              class="payment-slip__late"
              v-if="financialPositionData.late.length > 0"
            >
              <router-link
                v-if="financialPositionData.hasPayableLatePaymentSlips"
                :to="{ name: 'latePaymentSlip' }"
                tag="span"
                class="payment-slip__late__info late"
              >
                <i class="payment-slip__late__icon bexsicon-alert icon"></i>

                <span>
                  Você possui parcela(s) em atraso.
                  <b>
                    Para gerar o(s) boleto(s),
                    <span style="text-decoration: underline">clique aqui.</span>
                  </b>
                </span>
              </router-link>

              <router-link
                v-else
                :to="{ name: 'ticketForm' }"
                tag="span"
                class="payment-slip__late__info late"
              >
                <i class="payment-slip__late__icon bexsicon-alert icon"></i>

                <span>
                  {{ $t('pages.financialPosition.notPayableLateMessage') }}
                  <span
                    v-if="ticketCrmEnabled"
                    style="text-decoration: underline; font-weight: bold"
                  >
                    {{ $t('pages.paymentSlip.error.link') }}
                  </span>
                </span>
              </router-link>
            </div>

            <v-data-table
              :headers="lateHeaders"
              :items="financialPositionData.late"
              hide-actions
              :no-data-text="pageParams.emptyState.late"
              sort-icon="bexsicon-down"
            >
              <template slot="items" slot-scope="props">
                <td
                  v-for="(header, i) in lateHeaders"
                  :key="i"
                  :class="`text-xs-center ${
                    header.value === 'discountedValue'
                      ? 'error--text bold--text'
                      : ''
                  }`"
                >
                  {{ header.formatter(props.item[header.value]) }}
                </td>
              </template>
            </v-data-table>
          </template>
        </v-card-text>
      </v-card>

      <v-card class="mt-4 financial-card">
        <v-card-title class="topic__title financial--color">
          {{ $t('pages.financialPosition.toExpireValues') }}
        </v-card-title>

        <v-card-text class="topic__body">
          <screen-loading
            v-if="toExpireLoading"
            margin="20px 25%"
          ></screen-loading>

          <v-data-table
            v-else
            :headers="toExpireHeaders"
            :items="financialPositionData.toExpire"
            hide-actions
            sort-icon="bexsicon-down"
            :no-data-text="pageParams.emptyState.toExpire"
          >
            <template slot="items" slot-scope="props">
              <td class="text-xs-center">
                {{ formatUtcDate(props.item.expirationDate) }}
              </td>

              <td class="text-xs-center bold--text">
                {{ formatMoney(props.item.discountedValue) }}
                <v-tooltip
                  top
                  color="grey lighten-3"
                  max-width="300"
                  class="no-print"
                  v-if="props.item.allowToPay && !debitBlock"
                >
                  <span class="tooltip__text no-print">
                    {{ enums.financialPositionToExpireTooltip }}
                  </span>

                  <span slot="activator" class="no-print">
                    <i
                      class="bexsicon-info tooltip__icon"
                      style="padding: 0 10px"
                    />
                  </span>
                </v-tooltip>
              </td>

              <td class="text-xs-center no-print third-fit-in-mobile">
                <PaymentButton
                  v-if="props.item.allowToPay && !debitBlock"
                  :item="props.item"
                  :email="email"
                  :cellphone="cellphone"
                  :buttonClass="'financial--background financial__pay-btn'"
                  @openSlip="OnPaySlip(selectedUnit, props.item)"
                  :sendByEmail="OnSendSlipByEmailToExpire"
                  :sendBySms="OnSendSlipBySmsToExpire"
                />
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <v-card class="mt-4 mb-5 financial-card">
        <v-card-title class="topic__title financial--color">
          {{ $t('pages.financialPosition.paidValues') }}
        </v-card-title>

        <v-card-text class="topic__body">
          <screen-loading v-if="paidLoading" margin="20px 25%"></screen-loading>

          <v-data-table
            v-else
            :headers="paidHeaders"
            :items="financialPositionData.paid"
            hide-actions
            :no-data-text="pageParams.emptyState.paid"
            sort-icon="bexsicon-down"
          >
            <template slot="items" slot-scope="props">
              <td
                v-for="(header, i) in paidHeaders"
                :key="i"
                :class="`text-xs-center ${
                  header.value === 'total' ? 'financial--color bold--text' : ''
                }`"
              >
                {{ header.formatter(props.item[header.value]) }}
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>
  </module-page>
</template>

<script>
import bus from '@/utils/bus';
import enums from '../../utils/enums';
import ModulePage from '@/components/layout/Page';
import ScreenLoading from '@/components/loaders/ScreenLoading';
import toExpirePaymentSlipMixin from '@/mixins/modules/toExpirePaymentSlip';
import financialPositionMixin from '@/mixins/modules/financialPosition';
import notificationTypes from '@/utils/notificationTypes';
import getSlipUrl from '@/services/getSlipUrl';
import logTrace from '@/services/logTrace';
import PaymentButton from '@/components/financial/PaymentButton';
import {
  APP,
  TICKET_CRM,
  CONFIG,
  NOTIFICATIONS,
  USER,
  PAYMENT_SLIPS,
} from '../../utils/constants';

export default {
  name: 'financial-status',
  components: {
    ModulePage,
    ScreenLoading,
    PaymentButton,
  },
  mixins: [toExpirePaymentSlipMixin, financialPositionMixin],
  computed: {
    // financingStatusUrl() {
    //   return this.integrations.JUNIX_STATUS_URL
    //     ? `${this.integrations.JUNIX_STATUS_URL}?numeroContrato=${selectedContract.id}`
    //     : null;
    // },
    imobmeUser() {
      return this.$store.getters[USER.GET_IMOBME_DATA];
    },
    ticketCrmEnabled() {
      return this.$store.getters[TICKET_CRM.GET_ENABLED];
    },
    customBreadcrumbs() {
      const { title } = this.pageParams;
      const { contractTypes } = this.enums;
      const text = `${title}: ${this.getContractTitle(this.selectedContract)}`;
      return [{ text }];
    },
    lateHeaders() {
      return this.mobile ? this.lateHeadersMobile : this.lateHeadersDesktop;
    },
    toExpireHeaders() {
      return this.mobile
        ? this.toExpireHeadersMobile
        : this.toExpireHeadersDesktop;
    },
    paidHeaders() {
      return this.mobile ? this.paidHeadersMobile : this.paidHeadersDesktop;
    },
    texts() {
      return this.$store.getters[CONFIG.GET].texts || {};
    },
    integrations() {
      return this.$store.getters[CONFIG.GET_SHARED_INTEGRATIONS];
    },
    allowedContracts() {
      const { FINANCIAL_POSITION } = enums.financialModules;
      return this.getAllowedContracts(
        FINANCIAL_POSITION,
        this.selectedUnit.contracts
      );
    },
  },
  data() {
    return {
      pageParams: this.$store.getters[APP.PAGES].financialPosition,
      currentContract: null,
      lateHeadersDesktop: [
        {
          text: 'Vencimento',
          formatter: this.formatUtcDate,
          value: 'expirationDate',
          align: 'center',
        },
        {
          text: 'Valor',
          value: 'noChargeValue',
          formatter: this.formatMoney,
          align: 'center',
        },
        {
          text: 'Encargos',
          value: 'costs',
          formatter: this.formatMoney,
          align: 'center',
        },
        {
          text: 'Valor Atualizado',
          formatter: this.formatMoney,
          value: 'discountedValue',
          align: 'center',
        },
      ],
      lateHeadersMobile: [
        {
          text: 'Vencimento',
          formatter: this.formatUtcDate,
          value: 'expirationDate',
          align: 'center',
        },
        {
          text: 'Valor Atualizado',
          formatter: this.formatMoney,
          value: 'discountedValue',
          align: 'center',
        },
      ],
      toExpireHeadersDesktop: [
        { text: 'Vencimento', value: 'expirationDate', align: 'center' },
        { text: 'Valor', value: 'discountedValue', align: 'center' },
        { text: ' ', value: 'parcelCode', align: 'center', class: 'no-print' },
      ],
      toExpireHeadersMobile: [
        { text: 'Vencimento', value: 'expirationDate', align: 'center' },
        { text: 'Valor', value: 'discountedValue', align: 'center' },
        { text: ' ', value: 'parcelCode', align: 'center', class: 'no-print' },
      ],
      paidHeadersDesktop: [
        {
          text: 'Vencimento',
          formatter: this.formatUtcDate,
          value: 'expirationDate',
          align: 'center',
        },
        {
          text: 'Pagamento',
          formatter: this.formatUtcDate,
          value: 'paymentDate',
          align: 'center',
        },
        {
          text: 'Valor',
          formatter: this.formatMoney,
          value: 'discountedValue',
          align: 'center',
        },
        {
          text: 'Encargos',
          formatter: this.formatMoney,
          value: 'costs',
          align: 'center',
        },
        {
          text: 'Descontos',
          formatter: this.formatMoney,
          value: 'discount',
          align: 'center',
        },
        {
          text: 'Total',
          formatter: this.formatMoney,
          value: 'total',
          align: 'center',
        },
      ],
      paidHeadersMobile: [
        {
          text: 'Vencimento',
          formatter: this.formatUtcDate,
          value: 'expirationDate',
          align: 'center',
        },
        {
          text: 'Total',
          formatter: this.formatMoney,
          value: 'total',
          align: 'center',
        },
      ],
      hasPayableLatePaymentSlips: false,
      activeStatus: 'Ativo',
      email: '',
      cellphone: {
        countryCode: '+55',
        areaCode: '',
        number: '',
      },
    };
  },
  created() {
    bus.$on('unit:change', this.updateStatus);
    bus.$on('unit:update', this.updateStatus);

    this.email = this.user.email;
    if (this.imobmeUser && this.imobmeUser.cellphone) {
      const { number, areaCode, countryCode } = this.imobmeUser.cellphone;
      this.cellphone = { number, areaCode, countryCode: countryCode || '+55' };
    }

    this.getStatus();
  },
  beforeDestroy() {
    bus.$off('unit:change', this.updateStatus);
    bus.$off('unit:update', this.updateStatus);
  },
  methods: {
    async OnPaySlip(selectedUnit, slip) {
      await this.$store.dispatch(PAYMENT_SLIPS.OPEN_PAYMENT_SLIP, slip);

      const slipContract = this.selectedContract;
      const traceInfo = {
        contractId: slipContract.id,
        contractType: slipContract.type,
        detachedType: this.getContractTitle(slipContract),
        expirationDate: slip.expirationDate,
        serieId: slip.serieId,
        serieCode: slip.serieCode,
        parcelCode: slip.parcelCode,
        discountedValue: slip.discountedValue,
        ready: slip.ready,
        url: slip.slipUrl,
      };
      const traceAction =
        this.enums.logTraceActions.toExpirePaymentSlipDownloadFinancialPosition;
      this.createTrace(traceAction, traceInfo, this.currentContract.id);

      const notificationInfo = {
        ctx: this,
        notification: {
          id: slip.id.toString(),
          type: notificationTypes.TO_EXPIRE_SLIPS,
        },
      };
      this.$store.dispatch(NOTIFICATIONS.SAVE_VISUALIZED, notificationInfo);
    },
    async OnSendSlipByEmailToExpire(item, cellphone, fromHome = false) {
      return await this.sendSlipByEmailToExpire(
        { ...item, contractId: this.selectedContract.id },
        cellphone,
        fromHome
      );
    },
    async OnSendSlipBySmsToExpire(item, cellphone, fromHome = false) {
      return await this.sendSlipBySmsToExpire(
        { ...item, contractId: this.selectedContract.id },
        cellphone,
        fromHome
      );
    },

    updateStatus(unitObj = null) {
      this.getStatus();
    },

    async getStatus(contract = null) {
      if (!contract)
        contract = this.selectedUnit.contracts.find((o) => o.isValid);

      this.currentContract = contract;

      const tracePath = this.enums.logTraceActionRoutes['financialPosition'];
      if (tracePath) logTrace({ action: tracePath, contractId: contract.id });

      this.getFinancialPosition(contract, { getSlipsUrl: true });
    },

    print() {
      this.createTrace(
        this.enums.logTraceActions.financialPositionPrint,
        {},
        this.currentContract.id
      );
      window.print();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/breakpoint';

.third-fit-in-mobile {
  padding: 0 !important;
}

@media print {
  >>> .financial-card {
    width: 100%;
  }

  >>> .v-card {
    padding: 0;
    margin: 0;

    .v-card__title {
      padding: 0.5em 0.5em 0;
    }

    .v-card__text {
      padding: 0;
    }
  }

  >>> .v-table {
    padding: 0;
    margin: 0;
  }

  .financial__status-iframe {
    height: 75px;

    &__iframe {
      height: 75px !important;
      padding: 0 !important;
    }
  }
}

.payment-slip {
  &__late {
    background: var(--error-color);
    border-radius: 4px;
    color: white !important;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    text-align: center;
    cursor: pointer;
    padding: 6px 0;
    width: 99%;
    margin: auto;
    margin: 10px auto 15px;

    &__info {
      font-size: 12px;
      flex: 1;
      align-self: center;
      margin-right: 2px;

      span {
        font-weight: bold;
      }
    }

    &__icon {
      margin-top: 3px;
      margin-bottom: auto;
      margin-right: 7px;
      padding: 0;
      flex: 0.08;
    }
  }
}

.financial {
  &__base-date {
    display: flex;
    justify-content: space-between;
    margin: 10px 0;
    font-size: 12px;
    padding: 0 5px;
    color: var(--light-dark-color);

    @include breakpoint(mobile) {
      font-size: 14px;
    }

    &__print-btn {
      min-width: 40px;
      max-height: 28px;
      border-radius: 5px;
      padding: 5px 0 2px 0;

      @include breakpoint(mobile) {
        max-height: 25px;
        padding: 3px 0 2px 0;
      }
    }

    &__description {
      padding: 5px 12px;
      font-size: 10px;
      color: var(--label-color);

      @include breakpoint(mobile) {
        padding: 5px;
        width: 90%;
        margin-top: -16px;
      }
    }
  }

  >>> &__pay-btn {
    flex: 0.2;
    z-index: 1;
    height: 25px;
    min-width: 60px;
    font-size: 12px;
    margin: 0;
    padding: 5px 14px !important;
    border-radius: 5px;
    text-decoration: none;
    color: #fff;
    text-align: center;
    line-height: 25px;
  }

  &__status-iframe {
    &__iframe {
      width: 100%;
      border: none;
      height: 91px;
      padding: 1em;

      @include breakpoint(mobile) {
        height: 115px;
      }
    }
  }

  &__totals {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    text-align: center;
    background-color: #f1eeee;
    padding: 1em 1.2em;
    height: 60px;
    border: 5px solid #fff;

    &__value {
      display: inline;
      padding: 0 0.2em;
      font-weight: 700;
      color: var(--light-dark-color);
    }

    &__notation {
      left: 37.5%;
      font-size: 0.75em;
    }

    @media print {
      -ms-flex-preferred-size: 33.33333333333333% !important;
      flex-basis: 33.33333333333333% !important;
      -webkit-box-flex: 0 !important;
      -ms-flex-positive: 0 !important;
      flex-grow: 0 !important;
      max-width: 33.33333333333333% !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/financial/FinancialPosition.vue